import { useState, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { getProjectDetails } from '@/api/project-details/api';
import type { PreviousWeeklyUpdateRecord } from '@/api/project-details/mocks/projectHome.types';
import { getClientProject } from '@/api/projects/api';

export interface UseProjectDataParams {
  accessToken: string | null | undefined;
  isProjectScopedRoute: boolean;
  selectedClientId: string | undefined;
  selectedProjectId: string | undefined;
  /**
   * Skip the `include=metadata` fetch below — set when the current route already renders
   * `ProjectHomePage`, which fetches a superset of this data itself (`useProjectHome`,
   * `include=metadata,team,health,trends,audit,updates`) and reports the resolved names back up
   * via `onProjectMetadataResolved`. Avoids firing the same endpoint twice on that route.
   */
  skipMetadataFetch?: boolean;
}

export interface UseProjectDataReturn {
  projectSharepointReady: boolean | null;
  selectedProjectName: string | null;
  selectedClientName: string | null;
  previousWeeklyUpdate: PreviousWeeklyUpdateRecord | null;
  setPreviousWeeklyUpdate: Dispatch<SetStateAction<PreviousWeeklyUpdateRecord | null>>;
}

/**
 * Fetches project-level metadata (SharePoint readiness, project name) whenever the scoped
 * client/project route changes. Also owns the `previousWeeklyUpdate` record handed off from
 * ProjectHomePage to WeeklyUpdatePage.
 */
export function useProjectData({
  accessToken,
  isProjectScopedRoute,
  selectedClientId,
  selectedProjectId,
  skipMetadataFetch = false,
}: UseProjectDataParams): UseProjectDataReturn {
  const [projectSharepointReady, setProjectSharepointReady] = useState<boolean | null>(null);
  const [basicProjectName, setBasicProjectName] = useState<string | null>(null);
  const [selectedClientName, setSelectedClientName] = useState<string | null>(null);
  const [metadataProjectName, setMetadataProjectName] = useState<string | null>(null);
  const [previousWeeklyUpdate, setPreviousWeeklyUpdate] =
    useState<PreviousWeeklyUpdateRecord | null>(null);

  useEffect(() => {
    if (!accessToken || !isProjectScopedRoute || !selectedClientId || !selectedProjectId) {
      setProjectSharepointReady(null);
      setBasicProjectName(null);
      return;
    }

    let cancelled = false;
    void getClientProject(accessToken, selectedClientId, selectedProjectId)
      .then((project) => {
        if (cancelled) {
          return;
        }
        const ok = Boolean(
          project.sharepoint_site_id?.trim() &&
          project.sharepoint_drive_id?.trim() &&
          project.sharepoint_folder_item_id?.trim() &&
          project.sharepoint_folder_web_url?.trim(),
        );
        setProjectSharepointReady(ok);
        setBasicProjectName(project.project_name ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setProjectSharepointReady(null);
          setBasicProjectName(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, isProjectScopedRoute, selectedClientId, selectedProjectId]);

  // Separate, lightweight fetch for breadcrumb name resolution: the basic `getClientProject`
  // response above has no `client_name` field, and — for Snowflake-only projects with no
  // Postgres row — that endpoint 404s internally and returns `project_name: null` too (see
  // `router/clients.py` basic-path `NotFoundError` shell response). `include=metadata` is
  // Snowflake-primary (resolves even without a Postgres record) and returns both `clientName`
  // and `projectName`, so it backfills both gaps. It only ever sets `sharepointFolderWebUrl`
  // (not the other three SharePoint fields the effect above needs), so this must stay a second
  // call rather than replacing the one above. Skipped on the Project Home route — see
  // `skipMetadataFetch` doc comment.
  useEffect(() => {
    if (
      skipMetadataFetch ||
      !accessToken ||
      !isProjectScopedRoute ||
      !selectedClientId ||
      !selectedProjectId
    ) {
      setSelectedClientName(null);
      setMetadataProjectName(null);
      return;
    }

    let cancelled = false;
    void getProjectDetails(accessToken, selectedClientId, selectedProjectId, 'metadata')
      .then((details) => {
        if (!cancelled) {
          setSelectedClientName(details.projectHeader?.clientName ?? null);
          setMetadataProjectName(details.projectHeader?.projectName ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedClientName(null);
          setMetadataProjectName(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [skipMetadataFetch, accessToken, isProjectScopedRoute, selectedClientId, selectedProjectId]);

  return {
    projectSharepointReady,
    selectedProjectName: basicProjectName ?? metadataProjectName,
    selectedClientName,
    previousWeeklyUpdate,
    setPreviousWeeklyUpdate,
  };
}
